import React, { useEffect, useMemo, useCallback } from "react";
import DataProcessingVM from "../viewmodel/DataProcessingVM";
import { useLocation } from "react-router-dom";
import CTab from "../../../basecomp/newComponents/CTab";
import CStepper from "../../../basecomp/newComponents/CStepper";
import CUpload from "../../../basecomp/newComponents/CUpload";
import CLoader from "../../../basecomp/newComponents/CLoader";
import CCard from "../../../basecomp/newComponents/CCard";
import CDropdown from "../../../basecomp/newComponents/CDropdown";
import CAlert from "../../../basecomp/newComponents/CAlert";
import CButton from "../../../basecomp/newComponents/CButton";
import CHeader from "../../../basecomp/newComponents/CHeader";
import CTooltip from "../../../basecomp/newComponents/CTooltip";
import CBox from "../../../basecomp/newComponents/CBox";
import CContainer from "../../../basecomp/newComponents/CContainer";
import CPaper from "../../../basecomp/newComponents/CPaper";
import CText from "../../../basecomp/newComponents/CText";
import CDivider from "../../../basecomp/newComponents/CDivider";
import CGrid from "../../../basecomp/newComponents/CGrid";
import CThemeProvider from "../../../basecomp/newComponents/CThemeProvider";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CDialog from "../../../basecomp/components/CDialog";
import { conasTheme } from "../../../basecomp/newComponents/CTheme";
import CModal from "../../../basecomp/newComponents/CModal";
import CRadioGroup from "../../../basecomp/newComponents/CRadioButton";
import CircularProgress from "@mui/material/CircularProgress";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import CDateTimePicker from "../../../basecomp/components/CDateTimePicker";
import { Box } from "@mui/system";

// ─────────────────────────────────────────────────────────────────────────────
// Automation Section Component — wrapped with React.memo + custom comparator
// ─────────────────────────────────────────────────────────────────────────────
const AutomationSection = React.memo(
  ({
    automationPlantId,
    automationStartDate,
    automationEndDate,
    isAutomationProcessing,
    automationFileStats,
    plantOptions,
    isLoadingPlantOptions,
    isAutomationComplete,
    isLoadingAutomationCount,
    setAutomationPlantId,
    setAutomationStartDate,
    setAutomationEndDate,
    handleAutomationProcess,
    handleAutomationDownload,
    handleAutomationRefresh,
    handleFetchAutomationCount,
    automationModelId,
    setAutomationModelId,
    automationModelOptions,
    automationInputId,
    setAutomationInputId,
    automationInputOptions,
    isLoadingOptions,
    selectedTool,
    isE2EToolWithMandatoryModel,
    theme,
    classificationLangauge,
    setClassificationLangauge,
    classificationLangaugeOptions,
    automationBatchId,
  }) => {
    const filtersEnabled = !isAutomationProcessing;
    const endDateEnabled = filtersEnabled && Boolean(automationStartDate);

    const hasRequiredFilters = true;
    // Boolean(classificationLangauge || selectedTool !== "Classification");

    const hasMandatorySelections = isE2EToolWithMandatoryModel
      ? Boolean(automationModelId)
      : selectedTool === "Classification" || selectedTool === "Enrichment"
        ? true
        : true;

    const isProcessButtonDisabled =
      isAutomationProcessing || !hasMandatorySelections || !hasRequiredFilters;

    const isFetchButtonDisabled =
      isAutomationProcessing ||
      isLoadingAutomationCount ||
      !hasMandatorySelections ||
      !hasRequiredFilters;

    return (
      <CPaper
        elevation={2}
        sx={{
          height: "580px",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          p: 2,
          bgcolor: theme.palette.background.paper,
        }}
        theme={theme}
      >
        <CText
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: theme.palette.text.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          theme={theme}
        >
          <PlayArrowIcon sx={{ color: theme.palette.primary.main }} />
          End-to-End Automation
        </CText>

        {/* Upper Section — Form Fields */}
        <CBox
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateRows: "1fr 1fr",
            gap: 2,
            minHeight: 0,
          }}
          theme={theme}
        >
          <CPaper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRadius: 2,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
              px: 3,
              py: 2,
              bgcolor: theme.palette.background.default,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${theme.palette.secondary.light}08, ${theme.palette.secondary.main}12)`,
                zIndex: 0,
              },
            }}
            theme={theme}
          >
            <CBox
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
              }}
              theme={theme}
            >
              {/* ── ROW 1: Input Type + Plant ID ── */}
              {isE2EToolWithMandatoryModel ||
                selectedTool === "Classification" ||
                selectedTool === "Enrichment" ? (
                <Box
                  sx={{ display: "flex", flexDirection: "row", gap: 3, width: "100%" }}
                >

                  <Box sx={{ flex: 1 }}>
                    <CDropdown
                      selectedValue={automationPlantId}
                      onValueChange={setAutomationPlantId}
                      options={plantOptions || []}
                      label={
                        isLoadingPlantOptions
                          ? "Loading plants..."
                          : plantOptions?.length > 0
                            ? "Select Plant ID"
                            : "No Plants Available"
                      }
                      theme={theme}
                      disabled={
                        !filtersEnabled ||
                        isLoadingPlantOptions ||
                        !plantOptions ||
                        plantOptions.length === 0 ||
                        ["MPN-GT00", "SQ01 GT00"].includes(automationInputId)
                      }
                      showSearch={true}
                      clearable={true}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: theme.palette.background.paper,
                        },
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <CDropdown
                  selectedValue={automationPlantId}
                  onValueChange={setAutomationPlantId}
                  options={plantOptions || []}
                  label={
                    isLoadingPlantOptions
                      ? "Loading plants..."
                      : plantOptions?.length > 0
                        ? "Select Plant ID"
                        : "No Plants Available"
                  }
                  theme={theme}
                  disabled={
                    !filtersEnabled ||
                    isLoadingPlantOptions ||
                    !plantOptions ||
                    plantOptions.length === 0
                  }
                  showSearch={true}
                  clearable={true}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                    },
                  }}
                />
              )}


              {/* ── Language — Classification ONLY ── */}
              {selectedTool === "Classification" && (
                <CDropdown
                  selectedValue={classificationLangauge}
                  onValueChange={setClassificationLangauge}
                  options={classificationLangaugeOptions || []}
                  label={
                    isLoadingOptions
                      ? "Loading languages..."
                      : classificationLangaugeOptions?.length > 0
                        ? "Select Language"
                        : "No Languages Available"
                  }
                  theme={theme}
                  disabled={
                    isAutomationProcessing ||
                    isLoadingOptions ||
                    !classificationLangaugeOptions ||
                    classificationLangaugeOptions.length === 0
                  }
                  showSearch={true}
                  clearable={true}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                    },
                  }}
                />
              )}

              {/* ── ROW 3: Start Date + End Date ── */}
              <Box
                sx={{ display: "flex", flexDirection: "row", gap: 1, width: "100%" }}
              >
                <Box sx={{ flex: 1 }}>
                  <CDateTimePicker
                    cId="automation-start-date"
                    cOnChange={(value) => setAutomationStartDate(value)}
                    cDisabled={!filtersEnabled}
                    cHideTime={true}
                    cIsSession={false}
                    cHelperText="Start Date"
                    cSmall={true}
                    cFullWidth={true}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <CDateTimePicker
                    cId="automation-end-date"
                    cOnChange={(value) => setAutomationEndDate(value)}
                    cDisabled={!endDateEnabled}
                    cHideTime={true}
                    cIsSession={false}
                    cHelperText="End Date"
                    cSmall={true}
                    cFullWidth={true}
                  />
                </Box>
              </Box>

              {/* ── ROW 2: Model — Data Matching ONLY ── */}
              {isE2EToolWithMandatoryModel && (
                <CDropdown
                  selectedValue={automationModelId}
                  onValueChange={setAutomationModelId}
                  options={automationModelOptions || []}
                  label={
                    isLoadingOptions
                      ? "Loading models..."
                      : automationModelOptions?.length > 0
                        ? "Select Model"
                        : "No Models Available"
                  }
                  required={true}
                  theme={theme}
                  disabled={
                    isAutomationProcessing ||
                    isLoadingOptions ||
                    !automationModelOptions ||
                    automationModelOptions.length === 0
                  }
                  showSearch={true}
                  clearable={true}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                    },
                  }}
                />
              )}


              {/* ── FETCH RECORDS + PROCESS AUTOMATION BUTTONS ── */}
              <Box
                sx={{ display: "flex", flexDirection: "row", gap: 1, width: "100%" }}
              >
                <CButton
                  label={isLoadingAutomationCount ? "Fetching..." : "Fetch Records"}
                  onClick={handleFetchAutomationCount}
                  customIcon={
                    isLoadingAutomationCount ? (
                      <CircularProgress size={16} sx={{ color: "inherit" }} />
                    ) : (
                      <SearchIcon sx={{ fontSize: "1rem" }} />
                    )
                  }
                  iconPosition="start"
                  size="small"
                  disabled={isFetchButtonDisabled}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    flex: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 500,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: !isFetchButtonDisabled
                        ? `0 4px 12px ${theme.palette.primary.main}25`
                        : "none",
                    },
                  }}
                />
                <CButton
                  type="process"
                  label={
                    isAutomationProcessing ? "Processing..." : "Process Automation"
                  }
                  onClick={handleAutomationProcess}
                  customIcon={<PlayArrowIcon />}
                  iconPosition="start"
                  size="large"
                  disabled={isProcessButtonDisabled}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.95rem",
                    flex: 1,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 500,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: `0 4px 12px ${theme.palette.primary.main}25`,
                    },
                  }}
                />
              </Box>
            </CBox>
          </CPaper>

          {/* ── Lower Section — Statistics Card ── */}
          <CPaper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
              bgcolor:
                automationFileStats?.totalRecords > 0
                  ? `${theme.palette.success.main}08`
                  : theme.palette.background.default,
            }}
            theme={theme}
          >
            <CBox
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                gap: 1.5,
              }}
              theme={theme}
            >
              {isLoadingAutomationCount ? (
                <>
                  <CircularProgress
                    size={36}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <CText
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      mt: 1,
                    }}
                    theme={theme}
                  >
                    Fetching records...
                  </CText>
                </>
              ) : automationFileStats?.totalRecords > 0 ? (
                <>
                  <CText
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                    }}
                    theme={theme}
                  >
                    Count of Records Found
                  </CText>
                  <CText
                    variant="h3"
                    sx={{ fontWeight: 700, color: theme.palette.success.main }}
                    theme={theme}
                  >
                    {automationFileStats.totalRecords}
                  </CText>

                  {isAutomationComplete && (
                    <CBox sx={{ display: "flex", gap: 1, mt: 2 }} theme={theme}>
                      {selectedTool !== "Enrichment" && (
                        <CButton
                          type="download"
                          label="Download"
                          onClick={handleAutomationDownload}
                          customIcon={<FileDownloadIcon />}
                          iconPosition="start"
                          size="small"
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            px: 2,
                            py: 1,
                            borderRadius: 1.5,
                            minWidth: "100px",
                          }}
                        />
                      )}
                      <CButton
                        label="Refresh"
                        onClick={handleAutomationRefresh}
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          px: 2,
                          py: 1,
                          borderRadius: 1.5,
                          minWidth: "100px",
                        }}
                      />
                    </CBox>
                  )}
                </>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 56,
                      color: theme.palette.grey[400],
                      opacity: 0.5,
                      mb: 1,
                    }}
                  />
                  <CText
                    variant="body1"
                    sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                    theme={theme}
                  >
                    No data available
                  </CText>
                  <CText
                    variant="body2"
                    sx={{
                      color: theme.palette.text.disabled,
                      fontSize: "0.875rem",
                    }}
                    theme={theme}
                  >
                    Fill in the filters above and click{" "}
                    <strong>Fetch Records</strong> to load data. Model and input
                    type are required to process.
                  </CText>
                </>
              )}
            </CBox>
          </CPaper>
        </CBox>
      </CPaper>
    );
  },
  // ✅ Custom comparator — only re-render when these props actually change
  (prevProps, nextProps) => {
    return (
      prevProps.automationPlantId === nextProps.automationPlantId &&
      prevProps.automationStartDate === nextProps.automationStartDate &&
      prevProps.automationEndDate === nextProps.automationEndDate &&
      prevProps.automationModelId === nextProps.automationModelId &&
      prevProps.automationInputId === nextProps.automationInputId &&
      prevProps.automationModelOptions === nextProps.automationModelOptions &&
      prevProps.automationInputOptions === nextProps.automationInputOptions &&
      prevProps.plantOptions === nextProps.plantOptions &&
      prevProps.isAutomationProcessing === nextProps.isAutomationProcessing &&
      prevProps.isAutomationComplete === nextProps.isAutomationComplete &&
      prevProps.isLoadingOptions === nextProps.isLoadingOptions &&
      prevProps.isLoadingPlantOptions === nextProps.isLoadingPlantOptions &&
      prevProps.isLoadingAutomationCount === nextProps.isLoadingAutomationCount &&
      prevProps.automationFileStats === nextProps.automationFileStats &&
      prevProps.classificationLangauge === nextProps.classificationLangauge &&
      prevProps.classificationLangaugeOptions === nextProps.classificationLangaugeOptions &&
      prevProps.selectedTool === nextProps.selectedTool &&
      prevProps.isE2EToolWithMandatoryModel === nextProps.isE2EToolWithMandatoryModel &&
      prevProps.automationBatchId === nextProps.automationBatchId
    );
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Main View
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedDataProcessingView = () => {
  const {
    activeStep,
    toolTabValue,
    selectedTool,
    selectedFile,
    isProcessing,
    selectedModel,
    selectedCustomer,
    processingProgress,
    processingKey,
    processingStats,
    isMobile,
    totalCount,
    isUploading,
    uploadProgress,
    modelOptions,
    isLoadingOptions,
    cardData,
    toolOptions,
    tabConfig,
    errorDetails,
    showErrorModal,
    cOpen,
    sampleDialogOpen,
    selectedSampleType,
    sampleDownloadLoading,
    sampleTypeOptions,
    modelSelectionType,
    uploadedModelFile,
    isModelFileUploading,
    modelSelectionOptions,
    isModelSelected,
    uploadedModelS3Location,
    automationPlantId,
    automationStartDate,
    automationEndDate,
    isAutomationProcessing,
    automationFileStats,
    plantOptions,
    isLoadingPlantOptions,
    automationModelId,
    setAutomationModelId,
    automationModelOptions,
    automationInputId,
    setAutomationInputId,
    automationInputOptions,
    isE2EToolWithMandatoryModel,
    setAutomationPlantId,
    setAutomationStartDate,
    setAutomationEndDate,
    handleFileUpload,
    handleStartProcessing,
    handleDownload,
    handleRequestSample,
    handleReset,
    handleToolTabChange,
    handleModelChange,
    handleCustomerChange,
    handleViewHistory,
    handleFileSelection,
    handleErrorDialogClose,
    handleSampleDialogClose,
    handleConfirmSampleDownload,
    showInputSubOptions,
    selectedInputSubType,
    inputSubTypeOptions,
    handleSampleTypeSelection,
    handleInputSubTypeSelection,
    handleModelSelectionTypeChange,
    handleModelFileSelection,
    handleModelFileUpload,
    handleAutomationProcess,
    handleFetchAutomationCount,
    setCOpen,
    setSelectedFile,
    clearAllIntervals,
    setSelectedSampleType,
    setUploadedModelFile,
    setUploadedModelS3Location,
    isAutomationComplete,
    automationBatchId,
    handleAutomationDownload,
    handleAutomationRefresh,
    isLoadingAutomationCount,
    classificationLangaugeOptions,
    classificationLangauge,
    setClassificationLangauge,
  } = DataProcessingVM();

  const location = useLocation();

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ Stable array references — React.memo on AutomationSection needs these
  //    to be the same reference between renders, otherwise memo is bypassed.
  // ─────────────────────────────────────────────────────────────────────────
  const stableAutomationModelOptions = useMemo(
    () => automationModelOptions,
    [automationModelOptions]
  );

  const stableAutomationInputOptions = useMemo(
    () => automationInputOptions,
    [automationInputOptions]
  );

  const stablePlantOptions = useMemo(
    () => plantOptions,
    [plantOptions]
  );

  const stableClassificationLangaugeOptions = useMemo(
    () => classificationLangaugeOptions,
    [classificationLangaugeOptions]
  );

  // ✅ Stable object reference for file stats
  const stableAutomationFileStats = useMemo(
    () => ({
      totalRecords: automationFileStats.totalRecords,
      uniqueRecords: automationFileStats.uniqueRecords,
    }),
    [automationFileStats.totalRecords, automationFileStats.uniqueRecords]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ Stable handler references — new function reference on every render
  //    breaks React.memo even when data hasn't changed.
  // ─────────────────────────────────────────────────────────────────────────
  const stableHandleFetchAutomationCount = useCallback(
    handleFetchAutomationCount,
    [handleFetchAutomationCount]
  );

  const stableHandleAutomationProcess = useCallback(
    handleAutomationProcess,
    [handleAutomationProcess]
  );

  const stableHandleAutomationDownload = useCallback(
    handleAutomationDownload,
    [handleAutomationDownload]
  );

  const stableHandleAutomationRefresh = useCallback(
    handleAutomationRefresh,
    [handleAutomationRefresh]
  );

  // ─────────────────────────────────────────────────────────────────────────

  const renderCustomFileUpload = () => {
    if (!selectedFile) {
      return (
        <CBox
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            bgcolor: conasTheme.palette.background.default,
            position: "relative",
            cursor: isModelSelected() ? "pointer" : "not-allowed",
            opacity: isModelSelected() ? 1 : 0.6,
            transition: "all 0.3s ease",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isModelSelected()
                ? `linear-gradient(135deg, ${conasTheme.palette.primary.light}08, ${conasTheme.palette.primary.main}12)`
                : `linear-gradient(135deg, ${conasTheme.palette.grey[300]}08, ${conasTheme.palette.grey[400]}12)`,
              zIndex: 0,
            },
            "&:hover": {
              "&::before": {
                background: isModelSelected()
                  ? `linear-gradient(135deg, ${conasTheme.palette.primary.light}15, ${conasTheme.palette.primary.main}20)`
                  : `linear-gradient(135deg, ${conasTheme.palette.grey[300]}08, ${conasTheme.palette.grey[400]}12)`,
              },
            },
          }}
          theme={conasTheme}
          onClick={() => {
            if (isModelSelected()) {
              document.getElementById("data-file-input").click();
            }
          }}
        >
          <CBox sx={{ position: "relative", zIndex: 1 }} theme={conasTheme}>
            <CloudUploadIcon
              sx={{
                mt: -1,
                fontSize: 56,
                color: isModelSelected()
                  ? conasTheme.palette.primary.main
                  : conasTheme.palette.grey[400],
                mb: 0.5,
                opacity: 0.8,
              }}
            />
            <CText
              variant="h6"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: isModelSelected()
                  ? conasTheme.palette.text.primary
                  : conasTheme.palette.text.disabled,
              }}
              theme={conasTheme}
            >
              {isModelSelected()
                ? "Choose Data File"
                : "Please select a model first"}
            </CText>
            <CText
              variant="body2"
              sx={{
                mb: 2,
                color: isModelSelected()
                  ? conasTheme.palette.text.secondary
                  : conasTheme.palette.text.disabled,
                fontSize: "0.875rem",
              }}
              theme={conasTheme}
            >
              {isModelSelected()
                ? "Maximum 100,000 records • Only .xlsx files supported"
                : "Model selection required to enable upload"}
            </CText>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => handleFileSelection(e.target.files)}
              disabled={isUploading || !isModelSelected()}
              style={{ display: "none" }}
              id="data-file-input"
            />
            <CButton
              type="browse"
              label="Browse Files"
              customIcon={<CloudUploadIcon />}
              iconPosition="start"
              disabled={isUploading || !isModelSelected()}
              size="medium"
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: isModelSelected()
                    ? `0 4px 12px ${conasTheme.palette.primary.main}25`
                    : "none",
                },
              }}
            />
          </CBox>
        </CBox>
      );
    }

    return (
      <CBox
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 0,
          bgcolor: `${conasTheme.palette.success.main}08`,
          textAlign: "center",
        }}
        theme={conasTheme}
      >
        <CloudUploadIcon
          sx={{
            fontSize: 40,
            color: conasTheme.palette.success.main,
            mb: 2,
          }}
        />
        <CText
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1 }}
          theme={conasTheme}
        >
          {selectedFile.name}
        </CText>
        <CText
          variant="body2"
          color="textSecondary"
          sx={{ mb: 3, fontSize: "0.75rem" }}
          theme={conasTheme}
        >
          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
        </CText>
        <CBox
          sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}
          theme={conasTheme}
        >
          <CButton
            type="cancel"
            label="Remove"
            variant="outlined"
            color="error"
            onClick={() => setSelectedFile(null)}
            disabled={isUploading}
            size="small"
            sx={{
              minWidth: "70px",
              borderRadius: 1.5,
              textTransform: "none",
            }}
          />
          <CButton
            type="upload"
            label={isUploading ? "Uploading..." : "Upload"}
            onClick={handleFileUpload}
            disabled={isUploading}
            iconPosition="start"
            isUploading={isUploading}
            size="small"
            sx={{
              minWidth: "90px",
              borderRadius: 1.5,
              textTransform: "none",
            }}
          />
        </CBox>
      </CBox>
    );
  };

  const errorDialogContent = errorDetails && (
    <div style={{ marginTop: "-20px" }}>
      {errorDetails.message && (
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontWeight: "bold",
              color: "#D32F2F",
              fontSize: "18px",
              marginBottom: "8px",
            }}
          >
            Error:
          </p>
          <p style={{ fontSize: "16px" }}>{errorDetails.message}</p>
        </div>
      )}
      {errorDetails.missing_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontWeight: "bold",
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            Missing Required Columns:
          </p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.missing_columns.map((column, index) => (
              <li
                key={index}
                style={{ marginBottom: "4px", fontSize: "14px" }}
              >
                {column}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errorDetails.extra_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontWeight: "bold",
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            Extra Columns:
          </p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.extra_columns.map((column, index) => (
              <li
                key={index}
                style={{ marginBottom: "4px", fontSize: "14px" }}
              >
                {column}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errorDetails.expected_columns?.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontWeight: "bold",
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            Expected Columns:
          </p>
          <ul style={{ marginLeft: "20px" }}>
            {errorDetails.expected_columns.map((column, index) => (
              <li
                key={index}
                style={{ marginBottom: "4px", fontSize: "14px" }}
              >
                {column}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ marginTop: "20px", fontSize: "15px", color: "#555" }}>
        <p>Please correct your file format and try uploading again.</p>
      </div>
    </div>
  );

  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  const renderToolTabs = () => (
    <CBox
      sx={{ position: "relative" }}
      theme={conasTheme}
      style={{ marginTop: "5px" }}
    >
      <CTab
        value={toolTabValue}
        onChange={handleToolTabChange}
        tabs={tabConfig}
        isMobile={isMobile}
        theme={conasTheme}
      />
      <CBox
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          height: "100%",
          pointerEvents: "none",
        }}
        theme={conasTheme}
      >
        {tabConfig.map((tab, index) => {
          if (!tab.disabled)
            return <CBox key={index} sx={{ flex: 1 }} theme={conasTheme} />;
          const tabWidth = `${100 / tabConfig.length}%`;
          return (
            <CTooltip
              key={index}
              title="Work In Progress"
              arrow
              placement="top"
              theme={conasTheme}
              PopperProps={{ sx: { pointerEvents: "auto" } }}
            >
              <CBox
                sx={{
                  flex: 1,
                  height: "100%",
                  width: tabWidth,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "auto",
                  cursor: "not-allowed",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    zIndex: 1,
                  },
                }}
                theme={conasTheme}
              />
            </CTooltip>
          );
        })}
      </CBox>
    </CBox>
  );

  const renderDataMatchingControls = () => {
    if (selectedTool !== "Data Matching") return null;
    return (
      <CBox
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
        theme={conasTheme}
      >
        <CBox
          sx={{
            width: { xs: "100%", sm: "80%" },
            display: "flex",
            flexDirection: "column",
          }}
          theme={conasTheme}
        >
          <CText
            variant="subtitle2"
            sx={{
              mb: 1,
              ml: 1,
              fontWeight: "bold",
              fontSize: "0.875rem",
            }}
            theme={conasTheme}
          >
            Manual Processing Model Selection:
          </CText>
          <CBox
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
            theme={conasTheme}
          >
            <CRadioGroup
              label=""
              options={modelSelectionOptions}
              value={modelSelectionType}
              onChange={handleModelSelectionTypeChange}
              disabled={isUploading || isModelFileUploading}
              name="model-selection-type"
              sx={{
                width: "100%",
                "& .MuiFormGroup-root": {
                  flexDirection: "row !important",
                  gap: "24px !important",
                  justifyContent: "flex-start",
                  flexWrap: "nowrap",
                },
                "& .MuiFormControlLabel-root": {
                  margin: "0 !important",
                  whiteSpace: "nowrap",
                },
              }}
              theme={conasTheme}
            />
          </CBox>
        </CBox>
        <CBox
          sx={{
            width: { xs: "100%", sm: "20%" },
            display: "flex",
            alignItems: "end",
          }}
          theme={conasTheme}
        >
          <CButton
            onClick={handleViewHistory}
            type="history"
            label="History"
            sx={{ width: "100%", marginBottom: "0.9em" }}
            iconPosition="start"
            disabled={isUploading}
            size={isMobile ? "small" : "medium"}
          />
        </CBox>
      </CBox>
    );
  };

  const renderModelSelectionArea = () => {
    if (selectedTool !== "Data Matching") return null;
    return (
      <CBox
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        theme={conasTheme}
      >
        {modelSelectionType === "existing" ? (
          <CBox
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 4,
              bgcolor: conasTheme.palette.background.default,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${conasTheme.palette.primary.light}08, ${conasTheme.palette.primary.main}12)`,
                zIndex: 0,
              },
            }}
            theme={conasTheme}
          >
            <CBox
              sx={{
                position: "relative",
                zIndex: 1,
                width: "100%",
                textAlign: "center",
              }}
              theme={conasTheme}
            >
              <CText
                variant="h6"
                sx={{
                  mb: 4,
                  fontWeight: 600,
                  color: conasTheme.palette.text.primary,
                }}
                theme={conasTheme}
              >
                Select Existing Model
              </CText>
              <CDropdown
                selectedValue={selectedModel}
                onValueChange={handleModelChange}
                options={modelOptions || []}
                label={isLoadingOptions ? "Loading models..." : "Choose Model"}
                required={true}
                theme={conasTheme}
                disabled={isUploading || isModelFileUploading}
                showSearch={true}
                clearable={true}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: conasTheme.palette.background.paper,
                  },
                }}
              />
            </CBox>
          </CBox>
        ) : (
          <CBox
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            theme={conasTheme}
          >
            {!uploadedModelFile ? (
              <CBox
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  bgcolor: conasTheme.palette.background.default,
                  position: "relative",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${conasTheme.palette.secondary.light}08, ${conasTheme.palette.secondary.main}12)`,
                    zIndex: 0,
                  },
                  "&:hover": {
                    "&::before": {
                      background: `linear-gradient(135deg, ${conasTheme.palette.secondary.light}15, ${conasTheme.palette.secondary.main}20)`,
                    },
                  },
                }}
                theme={conasTheme}
                onClick={() =>
                  document.getElementById("model-file-input").click()
                }
              >
                <CBox
                  sx={{ position: "relative", zIndex: 1 }}
                  theme={conasTheme}
                >
                  <CloudUploadIcon
                    sx={{
                      mt: -2,
                      fontSize: 56,
                      color: conasTheme.palette.primary.main,
                      mb: 0,
                      opacity: 0.8,
                    }}
                  />
                  <CText
                    variant="h6"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: conasTheme.palette.text.primary,
                    }}
                    theme={conasTheme}
                  >
                    Choose Model File
                  </CText>
                  <CText
                    variant="body2"
                    sx={{
                      mb: 2,
                      color: conasTheme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                    theme={conasTheme}
                  >
                    Upload your custom .xlsx model file
                  </CText>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleModelFileSelection(e.target.files)}
                    disabled={isUploading || isModelFileUploading}
                    style={{ display: "none" }}
                    id="model-file-input"
                  />
                  <CButton
                    type="browse"
                    label="Browse Files"
                    customIcon={<CloudUploadIcon />}
                    iconPosition="start"
                    disabled={isUploading || isModelFileUploading}
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: "0.875rem",
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 500,
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: `0 4px 12px ${conasTheme.palette.primary.main}25`,
                      },
                    }}
                  />
                </CBox>
              </CBox>
            ) : (
              <CBox
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 3,
                  bgcolor: uploadedModelS3Location
                    ? `${conasTheme.palette.success.main}08`
                    : `${conasTheme.palette.warning.main}08`,
                  border: `2px solid ${uploadedModelS3Location
                      ? conasTheme.palette.success.light
                      : conasTheme.palette.warning.light
                    }`,
                  borderRadius: 2,
                  textAlign: "center",
                }}
                theme={conasTheme}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: 40,
                    color: uploadedModelS3Location
                      ? conasTheme.palette.success.main
                      : conasTheme.palette.warning.main,
                    mb: 2,
                  }}
                />
                <CText
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1 }}
                  theme={conasTheme}
                >
                  {uploadedModelFile.name}
                </CText>
                <CText
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 3, fontSize: "0.75rem" }}
                  theme={conasTheme}
                >
                  {(uploadedModelFile.size / 1024 / 1024).toFixed(2)} MB •{" "}
                  {uploadedModelS3Location
                    ? "Successfully uploaded"
                    : "Ready to upload"}
                </CText>
                <CBox
                  sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}
                  theme={conasTheme}
                >
                  <CButton
                    type="cancel"
                    label="Remove"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setUploadedModelFile(null);
                      setUploadedModelS3Location(null);
                    }}
                    disabled={isUploading || isModelFileUploading}
                    size="small"
                    sx={{
                      minWidth: "70px",
                      borderRadius: 1.5,
                      textTransform: "none",
                    }}
                  />
                  {!uploadedModelS3Location ? (
                    <CButton
                      type="upload"
                      label={isModelFileUploading ? "Uploading..." : "Upload"}
                      onClick={handleModelFileUpload}
                      disabled={isUploading || isModelFileUploading}
                      iconPosition="start"
                      isUploading={isModelFileUploading}
                      size="small"
                      sx={{
                        minWidth: "90px",
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    />
                  ) : (
                    <CButton
                      label="✓ Uploaded"
                      disabled={true}
                      size="small"
                      sx={{
                        minWidth: "90px",
                        borderRadius: 1.5,
                        backgroundColor: conasTheme.palette.success.main,
                        color: "white",
                        textTransform: "none",
                        "&.Mui-disabled": {
                          backgroundColor: conasTheme.palette.success.main,
                          color: "white",
                        },
                      }}
                    />
                  )}
                </CBox>
              </CBox>
            )}
          </CBox>
        )}
      </CBox>
    );
  };

  const renderFileInfo = () => {
    const params = new URLSearchParams(location.search);
    const urlBatchId = params.get("batchId");
    const urlToolName = params.get("toolName");
    const urlModelName = params.get("modelName");
    const urlCustomerName = params.get("customerName");
    const urlTotalRecords = params.get("totalRecords");
    const urlToBeProcessed = params.get("toBeProcessed") === "true";

    if (urlBatchId && urlToBeProcessed) {
      const formattedToolName = urlToolName
        ? urlToolName.replace(/[\s\-*]/g, "_")
        : selectedTool.replace(/[\s\-*]/g, "_");
      const formattedModelName = urlModelName
        ? urlModelName.replace(/[\s\-*]/g, "_")
        : selectedModel.replace(/[\s\-*]/g, "_");
      const fileName = `${formattedToolName}_${formattedModelName}.xlsx`;
      return (
        <CPaper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            bgcolor: conasTheme.palette.background.paper,
            borderColor: conasTheme.palette.divider,
            overflow: "auto",
          }}
          theme={conasTheme}
        >
          <CText variant="subtitle1" gutterBottom theme={conasTheme}>
            File Information
          </CText>
          <CGrid container spacing={2} theme={conasTheme}>
            <CGrid item xs={12} sm={6} theme={conasTheme}>
              <CText variant="body2" theme={conasTheme}>
                <strong>Tool:</strong> {urlToolName || selectedTool}
              </CText>
              <CText variant="body2" theme={conasTheme}>
                <strong>File Name:</strong> {fileName}
              </CText>
              {urlToolName !== "Enrichment" &&
                selectedTool !== "Enrichment" && (
                  <CText variant="body2" theme={conasTheme}>
                    <strong>Selected Model:</strong>{" "}
                    {urlModelName || selectedModel}
                  </CText>
                )}
              {(urlToolName === "Enrichment" ||
                selectedTool === "Enrichment") && (
                  <CText variant="body2" theme={conasTheme}>
                    <strong>Customer:</strong>{" "}
                    {urlCustomerName || selectedCustomer}
                  </CText>
                )}
            </CGrid>
            <CGrid item xs={12} sm={6} theme={conasTheme}>
              <CText variant="body2" theme={conasTheme}>
                <strong>Total Records:</strong>{" "}
                {urlTotalRecords || totalCount || 0}
              </CText>
              <CText variant="body2" theme={conasTheme}>
                <strong>Status:</strong> Ready to process
              </CText>
            </CGrid>
          </CGrid>
        </CPaper>
      );
    }

    if (!selectedFile) return null;

    const getModelDisplayInfo = () => {
      if (selectedTool === "Data Matching") {
        return modelSelectionType === "existing"
          ? selectedModel || ""
          : uploadedModelFile
            ? `Custom Model: ${uploadedModelFile.name}`
            : "";
      }
      if (selectedTool === "Enrichment") return selectedCustomer || "";
      return selectedModel || "";
    };

    return (
      <CPaper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          bgcolor: conasTheme.palette.background.paper,
          borderColor: conasTheme.palette.divider,
          overflow: "auto",
        }}
        theme={conasTheme}
      >
        <CText variant="subtitle1" gutterBottom theme={conasTheme}>
          File Information
        </CText>
        <CGrid container spacing={2} theme={conasTheme}>
          <CGrid item xs={12} sm={6} theme={conasTheme}>
            <CText variant="body2" theme={conasTheme}>
              <strong>Tool:</strong>{" "}
              {selectedTool || (toolOptions?.[toolTabValue]?.name ?? "")}
            </CText>
            <CText variant="body2" theme={conasTheme}>
              <strong>File Name:</strong>{" "}
              {selectedFile ? selectedFile.name : "example.xlsx"}
            </CText>
            {selectedTool !== "Enrichment" && (
              <CText variant="body2" theme={conasTheme}>
                <strong>Selected Model:</strong> {getModelDisplayInfo()}
              </CText>
            )}
            {selectedTool === "Enrichment" && (
              <CText variant="body2" theme={conasTheme}>
                <strong>Customer:</strong> {selectedCustomer || ""}
              </CText>
            )}
          </CGrid>
          <CGrid item xs={12} sm={6} theme={conasTheme}>
            <CText variant="body2" theme={conasTheme}>
              <strong>Total Records:</strong> {totalCount || 0}
            </CText>
            <CText variant="body2" theme={conasTheme}>
              <strong>Status:</strong> Ready to process
            </CText>
          </CGrid>
        </CGrid>
      </CPaper>
    );
  };

  const handleModalClose = () => {
    handleErrorDialogClose();
    setCOpen(false);
  };

  const isSplitLayoutTool = [
    "Mpn Segregation",
    "Data Matching",
    "Classification",
    "Enrichment",
  ].includes(selectedTool);

  return (
    <>
      <CBox
        sx={{
          bgcolor: conasTheme.palette.background.default,
          minHeight: "100vh",
          color: conasTheme.palette.text.primary,
          position: "relative",
        }}
        theme={conasTheme}
      >
        <CContainer
          maxWidth="lg"
          sx={{
            paddingTop: { xs: "4em", sm: "5em", md: "5em" },
            paddingLeft: { xs: 2, sm: 3 },
            paddingRight: { xs: 2, sm: 3 },
            pb: 5,
          }}
          theme={conasTheme}
        >
          <CBox
            sx={{
              pointerEvents: isUploading ? "none" : "auto",
              opacity: isUploading ? 0.7 : 1,
            }}
            theme={conasTheme}
          >
            {renderToolTabs()}
          </CBox>

          <CPaper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: { xs: 1, sm: 2 },
              bgcolor: conasTheme.palette.background.paper,
              overflow: "hidden",
            }}
            theme={conasTheme}
          >
            <CStepper
              activeStep={activeStep}
              isMobile={isMobile}
              theme={conasTheme}
            />

            <CBox sx={{ mb: 3, mt: 2 }} theme={conasTheme}>
              {/* ════════════════════ STEP 1 ════════════════════ */}
              {activeStep === 1 && (
                <CBox theme={conasTheme}>
                  <CBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                    theme={conasTheme}
                  >
                    <CHeader
                      variant="h6"
                      theme={conasTheme}
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    />
                  </CBox>

                  <CBox
                    sx={{ opacity: isUploading ? 0.6 : 1, transition: "opacity 0.3s" }}
                    theme={conasTheme}
                  >
                    {selectedTool === "Data Matching" &&
                      renderDataMatchingControls()}

                    {[
                      "Mpn Segregation",
                      "Classification",
                      "Enrichment",
                    ].includes(selectedTool) && (
                        <CBox
                          sx={{
                            mb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "-10px",
                            width: "100%",
                          }}
                          theme={conasTheme}
                        >
                          <CBox
                            sx={{
                              width: { xs: "100%", sm: "20%" },
                              display: "flex",
                              alignItems: "center",
                            }}
                            theme={conasTheme}
                          >
                            <CButton
                              onClick={handleViewHistory}
                              type="history"
                              label="History"
                              sx={{ width: "100%" }}
                              iconPosition="start"
                              disabled={isUploading}
                              size={isMobile ? "small" : "medium"}
                            />
                          </CBox>
                        </CBox>
                      )}

                    {/* ── SPLIT LAYOUT ── */}
                    {isSplitLayoutTool ? (
                      <CGrid
                        container
                        spacing={2}
                        sx={{ mb: 3, alignItems: "stretch" }}
                        theme={conasTheme}
                      >
                        {/* LEFT — E2E Automation */}
                        <CGrid item xs={12} md={6} theme={conasTheme}>
                          {/* ✅ All array/object props are stable refs (useMemo)
                              All handler props are stable refs (useCallback)
                              React.memo + custom comparator will skip re-render
                              when none of these props have changed           */}
                          <AutomationSection
                            key={selectedTool}
                            automationPlantId={automationPlantId}
                            automationStartDate={automationStartDate}
                            automationEndDate={automationEndDate}
                            isAutomationProcessing={isAutomationProcessing}
                            automationFileStats={stableAutomationFileStats}
                            plantOptions={stablePlantOptions}
                            isLoadingPlantOptions={isLoadingPlantOptions}
                            isAutomationComplete={isAutomationComplete}
                            isLoadingAutomationCount={isLoadingAutomationCount}
                            setAutomationPlantId={setAutomationPlantId}
                            setAutomationStartDate={setAutomationStartDate}
                            setAutomationEndDate={setAutomationEndDate}
                            handleAutomationProcess={stableHandleAutomationProcess}
                            handleAutomationDownload={stableHandleAutomationDownload}
                            handleAutomationRefresh={stableHandleAutomationRefresh}
                            handleFetchAutomationCount={stableHandleFetchAutomationCount}
                            automationBatchId={automationBatchId}
                            automationModelId={automationModelId}
                            setAutomationModelId={setAutomationModelId}
                            automationModelOptions={stableAutomationModelOptions}
                            automationInputId={automationInputId}
                            setAutomationInputId={setAutomationInputId}
                            automationInputOptions={stableAutomationInputOptions}
                            isLoadingOptions={isLoadingOptions}
                            selectedTool={selectedTool}
                            isE2EToolWithMandatoryModel={isE2EToolWithMandatoryModel}
                            classificationLangaugeOptions={stableClassificationLangaugeOptions}
                            setClassificationLangauge={setClassificationLangauge}
                            classificationLangauge={classificationLangauge}
                            theme={conasTheme}
                          />
                        </CGrid>

                        {/* RIGHT — Manual Processing */}
                        <CGrid item xs={12} md={6} theme={conasTheme}>
                          <CPaper
                            elevation={2}
                            sx={{
                              height: "580px",
                              display: "flex",
                              flexDirection: "column",
                              borderRadius: 3,
                              overflow: "hidden",
                              border: `1px solid ${conasTheme.palette.divider}`,
                              p: 2,
                              bgcolor: conasTheme.palette.background.paper,
                            }}
                            theme={conasTheme}
                          >
                            <CText
                              variant="h6"
                              sx={{
                                mb: 2,
                                fontWeight: 600,
                                color: conasTheme.palette.text.primary,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                              theme={conasTheme}
                            >
                              <CloudUploadIcon
                                sx={{ color: conasTheme.palette.primary.main }}
                              />
                              Manual Processing
                            </CText>

                            {selectedTool === "Data Matching" ? (
                              <CBox
                                sx={{
                                  flex: 1,
                                  display: "grid",
                                  gridTemplateRows: "1fr 1fr",
                                  gap: 2,
                                  minHeight: 0,
                                }}
                                theme={conasTheme}
                              >
                                <CPaper
                                  elevation={1}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: `1px solid ${conasTheme.palette.divider}`,
                                  }}
                                  theme={conasTheme}
                                >
                                  {renderModelSelectionArea()}
                                </CPaper>
                                <CPaper
                                  elevation={1}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: `1px solid ${conasTheme.palette.divider}`,
                                    position: "relative",
                                  }}
                                  theme={conasTheme}
                                >
                                  <CBox
                                    sx={{
                                      position: "relative",
                                      zIndex: 1,
                                      height: "100%",
                                      "& > div": {
                                        border: "none !important",
                                        borderRadius: "0 !important",
                                        backgroundColor:
                                          "transparent !important",
                                        height: "100%",
                                      },
                                    }}
                                    theme={conasTheme}
                                  >
                                    {renderCustomFileUpload()}
                                  </CBox>
                                </CPaper>
                              </CBox>
                            ) : (
                              <CBox
                                sx={{
                                  flex: 1,
                                  display: "grid",
                                  gridTemplateRows: "1fr 1fr",
                                  gap: 2,
                                  minHeight: 0,
                                }}
                                theme={conasTheme}
                              >
                                <CPaper
                                  elevation={1}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: `1px solid ${conasTheme.palette.divider}`,
                                    pl: 3,
                                    pr: 3,
                                    pb: 1,
                                    bgcolor:
                                      conasTheme.palette.background.default,
                                    position: "relative",
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: `linear-gradient(135deg, ${conasTheme.palette.primary.light}08, ${conasTheme.palette.primary.main}12)`,
                                      zIndex: 0,
                                    },
                                  }}
                                  theme={conasTheme}
                                >
                                  <CBox
                                    sx={{ position: "relative", zIndex: 1 }}
                                    theme={conasTheme}
                                  >
                                    <CText
                                      variant="h6"
                                      sx={{
                                        mb: 3,
                                        fontWeight: 600,
                                        color: conasTheme.palette.text.primary,
                                        textAlign: "center",
                                      }}
                                      theme={conasTheme}
                                    >
                                      Select{" "}
                                      {selectedTool === "Enrichment"
                                        ? "Customer"
                                        : "Model"}
                                    </CText>
                                    <CDropdown
                                      selectedValue={
                                        selectedTool === "Enrichment"
                                          ? selectedCustomer
                                          : selectedModel
                                      }
                                      onValueChange={
                                        selectedTool === "Enrichment"
                                          ? handleCustomerChange
                                          : handleModelChange
                                      }
                                      options={modelOptions || []}
                                      label={
                                        isLoadingOptions
                                          ? "Loading..."
                                          : `Choose ${selectedTool === "Enrichment"
                                            ? "Customer"
                                            : "Model"
                                          }`
                                      }
                                      required={true}
                                      theme={conasTheme}
                                      disabled={isUploading}
                                      showSearch={true}
                                      clearable={true}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: 2,
                                          bgcolor:
                                            conasTheme.palette.background.paper,
                                        },
                                      }}
                                    />
                                  </CBox>
                                </CPaper>
                                <CPaper
                                  elevation={1}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: `1px solid ${conasTheme.palette.divider}`,
                                  }}
                                  theme={conasTheme}
                                >
                                  <CBox
                                    sx={{
                                      height: "100%",
                                      "& > div": {
                                        border: "none !important",
                                        borderRadius: "0 !important",
                                        backgroundColor:
                                          "transparent !important",
                                        height: "100%",
                                      },
                                    }}
                                    theme={conasTheme}
                                  >
                                    <CUpload
                                      onFileSelection={handleFileSelection}
                                      onUpload={handleFileUpload}
                                      theme={conasTheme}
                                      maxRecords={
                                        selectedTool === "Classification"
                                          ? "70,000"
                                          : selectedTool === "Enrichment"
                                            ? "5,000"
                                            : selectedTool === "Mpn Segregation"
                                              ? "35,000"
                                              : "25,000"
                                      }
                                      selectedFile={selectedFile}
                                      onClearFile={() => setSelectedFile(null)}
                                      disabled={isUploading}
                                      modelSelected={isModelSelected()}
                                      disableClearButton={isUploading}
                                      isUploading={isUploading}
                                    />
                                  </CBox>
                                </CPaper>
                              </CBox>
                            )}
                          </CPaper>
                        </CGrid>
                      </CGrid>
                    ) : (
                      // ── Other tools (Duplicate Check, Alternate Manufacturer) ──
                      <CBox theme={conasTheme}>
                        <CBox
                          sx={{
                            mb: 2,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 2,
                            marginTop: "-10px",
                          }}
                          theme={conasTheme}
                        >
                          {selectedTool === "Duplicate Check" ? (
                            <CBox
                              sx={{ width: { xs: "100%", sm: "80%" } }}
                              theme={conasTheme}
                            >
                              <CDropdown
                                selectedValue=""
                                onValueChange={() => { }}
                                options={[]}
                                label="No Model Selection Required"
                                theme={conasTheme}
                                disabled={true}
                                showSearch={false}
                                clearable={false}
                              />
                            </CBox>
                          ) : (
                            <CBox
                              sx={{ width: { xs: "100%", sm: "80%" } }}
                              theme={conasTheme}
                            >
                              <CDropdown
                                selectedValue={selectedModel}
                                onValueChange={handleModelChange}
                                options={modelOptions || []}
                                label={
                                  isLoadingOptions
                                    ? "Loading options..."
                                    : "Select Model"
                                }
                                required={true}
                                theme={conasTheme}
                                disabled={isUploading}
                                showSearch={true}
                                clearable={true}
                              />
                            </CBox>
                          )}
                          <CBox
                            sx={{
                              width: { xs: "100%", sm: "20%" },
                              display: "flex",
                              alignItems: "center",
                            }}
                            theme={conasTheme}
                          >
                            <CButton
                              onClick={handleViewHistory}
                              type="history"
                              label="History"
                              sx={{ width: "100%" }}
                              iconPosition="start"
                              disabled={isUploading}
                              size={isMobile ? "small" : "medium"}
                            />
                          </CBox>
                        </CBox>

                        {renderModelSelectionArea()}

                        <CUpload
                          onFileSelection={handleFileSelection}
                          onUpload={handleFileUpload}
                          theme={conasTheme}
                          maxRecords={
                            selectedTool === "alternate Manufacturer"
                              ? "1,000"
                              : selectedTool === "Duplicate Check"
                                ? "50,000"
                                : "25,000"
                          }
                          selectedFile={selectedFile}
                          onClearFile={() => setSelectedFile(null)}
                          disabled={isUploading}
                          modelSelected={isModelSelected()}
                          disableClearButton={isUploading}
                          isUploading={isUploading}
                        />
                      </CBox>
                    )}
                  </CBox>

                  {isUploading && (
                    <CLoader
                      key={processingKey}
                      progress={uploadProgress}
                      stats={processingStats}
                      status="Uploading"
                      theme={conasTheme}
                      hideTimeRemaining={false}
                    />
                  )}

                  <CDivider sx={{ mb: 2 }} theme={conasTheme} />

                  <CBox
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                    theme={conasTheme}
                  >
                    <CButton
                      type="downloadSample"
                      onClick={handleRequestSample}
                      disabled={isUploading}
                      iconPosition="start"
                      size={isMobile ? "small" : "medium"}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                  </CBox>
                </CBox>
              )}

              {/* ════════════════════ STEP 2 ════════════════════ */}
              {activeStep === 2 && (
                <CBox theme={conasTheme}>
                  <CBox sx={{ mb: 2 }} theme={conasTheme}>
                    <CHeader
                      variant="h6"
                      theme={conasTheme}
                      gutterBottom
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    />
                    <CAlert
                      severity="success"
                      message="File uploaded successfully! You can now start the processing."
                      theme={conasTheme}
                    />
                  </CBox>

                  {renderFileInfo()}
                  

                  {isProcessing && (
                    <CLoader
                      key={processingKey}
                      progress={processingProgress}
                      stats={processingStats}
                      status="Processing"
                      theme={conasTheme}
                      hideTimeRemaining={false}

                    />
                  )}

                  <CBox
                    sx={{
                      opacity: isProcessing ? 0.6 : 1,
                      transition: "opacity 0.3s",
                      pointerEvents: isProcessing ? "none" : "auto",
                    }}
                    theme={conasTheme}
                  >
                    <CBox
                      sx={{
                        display: "flex",
                        justifyContent: { xs: "center", sm: "flex-end" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2,
                      }}
                      theme={conasTheme}
                    >
                      {selectedTool !== "Enrichment" && (
                        <CButton
                          type="back"
                          onClick={handleReset}
                          disabled={isProcessing}
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                          iconPosition="start"
                          size={isMobile ? "small" : "medium"}
                        />
                      )}
                      <CButton
                        type="process"
                        onClick={handleStartProcessing}
                        disabled={isProcessing}
                        isProcessing={isProcessing}
                        iconPosition="start"
                        size={isMobile ? "small" : "medium"}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      />
                    </CBox>
                  </CBox>
                </CBox>
              )}

              {/* ════════════════════ STEP 3 ════════════════════ */}
              {activeStep === 3 && (
                <CBox theme={conasTheme}>
                  <CBox sx={{ mb: 2 }} theme={conasTheme}>
                    <CHeader
                      title="Processing Results"
                      variant="h6"
                      theme={conasTheme}
                      gutterBottom
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    />
                    <CAlert
                      severity="success"
                      message="Processing complete! You can now download the results."
                      theme={conasTheme}
                    />
                  </CBox>

                  <CPaper
                    variant="outlined"
                    sx={{
                      p: { xs: 2, sm: 3 },
                      mb: 3,
                      bgcolor: conasTheme.palette.background.paper,
                      borderColor: conasTheme.palette.divider,
                      overflow: "auto",
                    }}
                    theme={conasTheme}
                  >
                    <CHeader
                      title="Processing Summary"
                      variant="subtitle1"
                      theme={conasTheme}
                      sx={{ mt: -1 }}
                      gutterBottom
                    />
                    <CCard cards={cardData || []} theme={conasTheme} />
                  </CPaper>

                  <CBox
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                    theme={conasTheme}
                  >
                    <CButton
                      type="reset"
                      onClick={handleReset}
                      size={isMobile ? "small" : "medium"}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                    <CButton
                      type="download"
                      onClick={handleDownload}
                      iconPosition="start"
                      size={isMobile ? "small" : "medium"}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                  </CBox>
                </CBox>
              )}
            </CBox>
          </CPaper>
        </CContainer>
      </CBox>

      {/* Sample Download Modal */}
      {selectedTool === "Data Matching" && (
        <CModal
          open={sampleDialogOpen}
          handleClose={handleSampleDialogClose}
          title="Download Sample Template"
          maxWidth="sm"
          fullWidth={true}
          actions={[
            {
              label: "Cancel",
              onClick: handleSampleDialogClose,
              disabled: sampleDownloadLoading,
              variant: "outlined",
            },
            {
              label: sampleDownloadLoading ? "Downloading..." : "Download",
              onClick: handleConfirmSampleDownload,
              disabled:
                sampleDownloadLoading ||
                !selectedSampleType ||
                (selectedTool === "Data Matching" &&
                  selectedSampleType === "input" &&
                  !selectedInputSubType),
              startIcon: sampleDownloadLoading ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              ),
              color: "primary",
            },
          ]}
        >
          <CBox sx={{ p: 2 }} theme={conasTheme}>
            <CText variant="body1" sx={{ mb: 2 }} theme={conasTheme}>
              Please select the type of sample template you want to download:
            </CText>
            <CRadioGroup
              label=""
              options={sampleTypeOptions}
              value={selectedSampleType}
              onChange={(value) => handleSampleTypeSelection(value)}
              disabled={sampleDownloadLoading}
              name="sample-type"
              sx={{ mt: 1, mb: showInputSubOptions ? 2 : 0 }}
              theme={conasTheme}
            />
            {showInputSubOptions && (
              <CBox theme={conasTheme}>
                <CText variant="body1" sx={{ mb: 2 }} theme={conasTheme}>
                  Please select the input sample format:
                </CText>
                <CRadioGroup
                  label=""
                  options={inputSubTypeOptions}
                  value={selectedInputSubType}
                  onChange={(value) => handleInputSubTypeSelection(value)}
                  disabled={sampleDownloadLoading}
                  name="input-sub-type"
                  sx={{ mt: 1 }}
                  theme={conasTheme}
                />
              </CBox>
            )}
          </CBox>
        </CModal>
      )}

      {isAutomationProcessing && (
        <CLoader
          key={processingKey}
          progress={processingProgress}
          stats={processingStats}
          status="Processing"
          theme={conasTheme}
          hideTimeRemaining={false}
        />
      )}

      <CBackDrop
        cOpen={cOpen && !isAutomationProcessing}
        cHandleClose={() => setCOpen(false)}
        cAllowClickToClose={false}
      />

      {errorDetails && (
        <CDialog
          cOpen={showErrorModal}
          cHandleClose={handleModalClose}
          cTitle="File Format Error"
          cDialogContent={errorDialogContent}
          cDialogWidth="sm"
          error={true}
          cDialogActions={[
            { label: "Close", onClick: handleModalClose, color: "primary" },
          ]}
        />
      )}
    </>
  );
};

export default EnhancedDataProcessingView;